import React, { Fragment, useEffect, useState } from 'react'
import {
  EditOutlined,
  DeleteOutlined,
  CheckCircleTwoTone,
  EyeOutlined
} from '@ant-design/icons'
import { withTranslation } from 'react-i18next'
import {
  Row,
  Col,
  Popconfirm,
  Spin,
  message,
  Modal,
  Button,
  Select,
  InputNumber,
  Empty
} from 'antd'
import history from '../../../history'
import './styles.scss'

const TriviaList = props => {
  const {
    t,
    triviaList,
    isLoading,
    triviaActions,
    user: {
      info: { active_organization_id }
    }
  } = props
  const [loading, setLoading] = useState(false)
  const [aiModalVisible, setAiModalVisible] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [numQuestions, setNumQuestions] = useState(null)
  const [categoryList, setCategoryList] = useState([])
  const [aiLoading, setAiLoading] = useState(false)
  const [aiQuestions, setAiQuestions] = useState([])
  const [aiSessionId, setAiSessionId] = useState(null)
  const [noData, setNoData] = useState(null)
  const [savedQuestion, setSavedQuestion] = useState([])
  const [usedAiIndices, setUsedAiIndices] = useState(new Set())

  useEffect(() => {
    getData('INIT')
    fetchCategories()
    // Combine saved state from navigation and sessionStorage
    try {
      const location = history.location
      const state = location && location.state
      const fromNav = state && state.questionID ? [state.questionID] : []
      const savedRaw = sessionStorage.getItem('aiSavedQuestions')
      const fromStorage = savedRaw ? JSON.parse(savedRaw) : []
      const combined = Array.isArray(fromStorage)
        ? [...fromNav, ...fromStorage]
        : [...fromNav]
      if (combined.length) {
        setSavedQuestion(prev =>
          Array.from(new Set([...(prev || []), ...combined]))
        )
      }
    } catch (e) {}
    const raw = sessionStorage.getItem('aiTriviaSession')
    if (raw) {
      try {
        const session = JSON.parse(raw)
        if (session && session.questions && session.questions.length > 0) {
          setAiQuestions(session.questions)
          setSelectedCategory(session.categoryId)
          setNumQuestions(session.numQuestions)
          setAiSessionId(session.sessionId)
          setAiModalVisible(true)
        }
      } catch (e) {
        // ignore parse errors
      }
    }
  }, [])
  const persistAiSession = session => {
    try {
      sessionStorage.setItem('aiTriviaSession', JSON.stringify(session))
    } catch (e) {
      // ignore storage errors
    }
  }

  useEffect(() => {
    try {
      sessionStorage.setItem('aiSavedQuestions', JSON.stringify(savedQuestion))
    } catch (e) {}
  }, [savedQuestion])

  const clearAiSession = () => {
    try {
      sessionStorage.removeItem('aiTriviaSession')
      sessionStorage.removeItem('aiSavedQuestions')
    } catch (e) {}
    setNoData(null)
    setNumQuestions(null)
    setAiQuestions([])
    setSavedQuestion([])
    setAiSessionId(null)
    setSelectedCategory(null)
    setUsedAiIndices(new Set())
  }

  const fetchCategories = async () => {
    if (props && props.getCategories) {
      let data = await props.getCategories()
      setCategoryList(data)
    }
  }
  const getData = async type => {
    let res = await triviaActions.fetchQuestions(type)
    if (res.status >= 300) {
      message.error(res.data.errorMessage)
    }
  }
  const deleteQuestion = async question => {
    setLoading(true)
    let res = await triviaActions.deleteQuestion(question.id)
    if (res.status < 300) {
      message.success('Question deleted successfully')
      getData('DELETE')
    }
    setLoading(false)
  }
  const addEditQuestion = (type, question) => {
    if (type === 'ADD') {
      history.push(`/share-sphere/add`)
    } else if (type === 'EDIT') {
      history.push(`/share-sphere/${question.id}`)
    }
  }
  const convertQuiz = async (items, categories) => {
    const letterOrder = ['A', 'B', 'C', 'D']

    return items.map((item, idx) => {
      const optionsArray = letterOrder
        .map(L =>
          item && item.options && item.options[L] ? item.options[L] : null
        )
        .filter(Boolean)

      const correctOption =
        item && item.options && item.correct_answer
          ? item.options[item.correct_answer]
          : null
      return {
        organization_id: active_organization_id,
        question: item && item.question ? item.question : '',
        options: optionsArray,
        correct_option: correctOption,
        main_image:
          item && item.question_image ? item.question_image.alt : undefined,
        main_image_url:
          item && item.question_image ? item.question_image.url : undefined,
        clarification_image:
          item && item.correct_answer_image
            ? item.correct_answer_image.alt
            : undefined,
        clarification_image_url:
          item && item.correct_answer_image
            ? item.correct_answer_image.url
            : undefined,
        categories
      }
    })
  }
  const handleGenerateAI = async () => {
    let categories = categoryList.find(el => el.id === selectedCategory)
    let payload = {
      category: categories.name,
      num_questions: numQuestions,
      org_id: active_organization_id
    }
    try {
      setNoData(null)
      setAiQuestions([])
      setAiSessionId(null)
      setAiLoading(true)
      let res = await triviaActions.fetchAIQuestions(payload)
      if (
        res &&
        res.status < 300 &&
        res.data &&
        res.data.questions &&
        res.data.questions.length > 0
      ) {
        let allQuestion = await convertQuiz(res.data.questions, [categories])
        setAiQuestions(allQuestion)
        const sessionId = Date.now().toString()
        setAiSessionId(sessionId)
        persistAiSession({
          sessionId: sessionId,
          categoryId: selectedCategory,
          questions: allQuestion
        })
      } else {
        setNoData(
          'No questions available. Try a new category or modify the number requested.'
        )
      }
    } catch (error) {
      message.error('Failed to geenerate Ai questions!!', error)
    } finally {
      setAiLoading(false)
    }
  }

  const openPrefilledEditor = q => {
    let categories = categoryList.find(el => el.id === selectedCategory)
    const optionMap = {
      A: q.options[0],
      B: q.options[1],
      C: q.options[2],
      D: q.options[3]
    }
    const correctAnswerLetter = Object.keys(optionMap).find(
      key => optionMap[key] === q.correct_option
    )

    let storedPayload = {
      category: categories.name,
      org_id: active_organization_id,
      store_questions: true,
      questions: [
        {
          question: q.question,
          options: optionMap,
          correct_answer: correctAnswerLetter
        }
      ]
    }
    history.push('/share-sphere/add', {
      aiPrefillQuestion: q,
      isAiQuestion: true,
      storedPayload: storedPayload,
      questionID: q.question
    })
    setAiModalVisible(false)
  }

  return (
    <div className="list-wapper">
      <div className="trivia">
        <section className="section header">
          <Row type="flex" justify="space-between">
            <Col sm={24} md={12} lg={12}>
              <div className="title">{t('v3:share_sphere')}</div>
            </Col>
            <Col sm={24} md={12} lg={12}>
              <div className="header__actions">
                <Button type="primary" onClick={() => setAiModalVisible(true)}>
                  {t('v5:generate_trivia_with_ai')}
                </Button>
                <Button
                  type="primary"
                  onClick={() => {
                    addEditQuestion('ADD')
                  }}
                >
                  {t('v4:add_a_question')}
                </Button>
              </div>
            </Col>
          </Row>
        </section>
        {/* AI Modal */}
        <Modal
          title={t('v5:generate_trivia_with_ai')}
          visible={aiModalVisible}
          onCancel={() => {
            clearAiSession()
            setAiModalVisible(false)
          }}
          footer={null}
          width={700}
          maskClosable={false}
        >
          {!aiQuestions.length && (
            <Fragment>
              <div className="ai-form">
                <div className="ai-form__group">
                  <div className="ai-form__label">
                    {t('v5:select_category')}
                  </div>
                  <Select
                    className="ai-form__control"
                    placeholder="Select category"
                    value={selectedCategory}
                    onChange={setSelectedCategory}
                  >
                    {categoryList.map(item => (
                      <Select.Option key={item.id} value={item.id}>
                        {item.name}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
                <div className="ai-form__group">
                  <div className="ai-form__label">
                    {t('v5:number_of_question')}
                  </div>
                  <InputNumber
                    min={1}
                    max={20}
                    value={numQuestions}
                    onChange={setNumQuestions}
                    className="ai-form__control"
                  />
                </div>
                <div className="ai-form__actions">
                  <Button
                    type="primary"
                    loading={aiLoading}
                    onClick={handleGenerateAI}
                    disabled={!selectedCategory || !numQuestions}
                    block
                  >
                    {t('v3:generate')}
                  </Button>
                </div>
              </div>
            </Fragment>
          )}
          {noData && (
            <div className="ai-empty">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={noData}
              />
            </div>
          )}
          {aiQuestions.length > 0 && (
            <div className="ai-section">
              <div className="ai-section__header">
                <h4 className="ai-section__title">
                  {t('v5:generated_questions')}
                </h4>
              </div>

              <div className="ai-section__list">
                {aiQuestions.map((q, idx) => {
                  const isClicked = usedAiIndices.has(idx)
                  const isSaved = savedQuestion.includes(q.question)
                  return (
                    <div
                      key={q.question || `ai-${q.question}`}
                      className={`ai-card${
                        isClicked ? ' ai-card--clicked' : ''
                      } ${isSaved ? ' ai-item-saved' : ''}`}
                    >
                      <div className="ai-card__content">
                        <div className="ai-card__index">{`Q${idx + 1}`}</div>
                        <div className="ai-card__question">{q.question}</div>
                      </div>
                      <div className="ai-card__action">
                        {isSaved || isClicked ? (
                          <span className="ai-card__saved">
                            <CheckCircleTwoTone twoToneColor="#52c41a" />
                            <span className="ai-card__saved-text">
                              {t('buttons:saved')}
                            </span>
                          </span>
                        ) : (
                          <Button
                            type="primary"
                            size="small"
                            shape="round"
                            aria-label="View"
                            onClick={() => openPrefilledEditor(q)}
                          >
                            <EyeOutlined />
                          </Button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
              <div style={{ textAlign: 'right', marginTop: 12 }}>
                <Button
                  type="default"
                  shape="round"
                  onClick={() => {
                    setAiModalVisible(false)
                    clearAiSession()
                  }}
                >
                  {t('v3:close')}
                </Button>
                &nbsp;
                <Popconfirm
                  title={t('v5:this_will_clear_all_generate_question')}
                  onConfirm={clearAiSession}
                  okText={t('buttons:clear')}
                  cancelText={t('v4:cancel')}
                  placement="left"
                >
                  <Button type="primary" shape="round">
                    {t('buttons:clear')}
                  </Button>
                </Popconfirm>
              </div>
            </div>
          )}
        </Modal>
        <section className="section question">
          {isLoading ? (
            <Row
              type="flex"
              justify="center"
              align="middle"
              className="spinner-wrap"
            >
              <Spin spinning />
            </Row>
          ) : (
            <Row gutter={[16, 16]}>
              {triviaList.length > 0 ? (
                <Fragment>
                  {triviaList.map((question, i) => {
                    return (
                      <Col md={24} key={i}>
                        <div className="question_body">
                          <div className="question_body_text">
                            {i + 1}.&nbsp;{question.question}
                          </div>
                          <div className="question_body_action">
                            <EditOutlined
                              onClick={() => {
                                !loading && addEditQuestion('EDIT', question)
                              }}
                              className="question_body_action_edit"
                            />
                            &nbsp;&nbsp;
                            <Popconfirm
                              title={t('v5:sure_delete')}
                              description={t('v5:delete_this_question')}
                              onConfirm={() =>
                                !loading && deleteQuestion(question)
                              }
                              okText={t('v4:yes')}
                              cancelText={t('v4:no')}
                              icon={false}
                            >
                              <DeleteOutlined className="question_body_action_delete" />
                            </Popconfirm>
                          </div>
                        </div>
                      </Col>
                    )
                  })}
                </Fragment>
              ) : (
                <Col md={24}>
                  <div className="question_body">
                    <div className="question_body_text">
                      {t('v4:travia_question_empty')}
                    </div>
                  </div>
                </Col>
              )}
            </Row>
          )}
        </section>
      </div>
    </div>
  )
}

export default withTranslation('trivia')(TriviaList)
